import { LenderApprovalStatus, ProcessingLenderStrategyType, StopCondition } from '@nemuru-eng/js-api-client';
import { ChooseStrategyType } from '@nemuru-eng/js-api-client/slim';

import { LoanApplicationEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/entities/LoanApplication/LoanApplicationEntity';
import { AbstractProcessingStrategyEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/entities/ProcessingStrategy/AbstractProcessingStrategyEntity';
import { RailRow } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/types/RailRow';
import { AbstractLoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/entities/PaymentMethodDetail/AbstractLoanPaymentMethodDetailEntity';

export class WaterfallProcessingStrategyEntity extends AbstractProcessingStrategyEntity {
  private readonly DEFAULT_STOP_CONDITION = StopCondition.MAYBE;

  private readonly stopConditionMap = new Map<StopCondition, LenderApprovalStatus[]>([
    [StopCondition.ERROR, [LenderApprovalStatus.NO, LenderApprovalStatus.MAYBE, LenderApprovalStatus.YES]],
    [StopCondition.NO, [LenderApprovalStatus.NO, LenderApprovalStatus.MAYBE, LenderApprovalStatus.YES]],
    [StopCondition.MAYBE, [LenderApprovalStatus.MAYBE, LenderApprovalStatus.YES]],
    [StopCondition.YES, [LenderApprovalStatus.YES]],
  ]);

  constructor(paymentMethodDetailEntity: AbstractLoanPaymentMethodDetailEntity) {
    super(paymentMethodDetailEntity);
  }

  public static isType(strategyType: ProcessingLenderStrategyType): boolean {
    return strategyType === ProcessingLenderStrategyType.WATERFALL;
  }

  protected sortLoanApplications(loanApplications: LoanApplicationEntity[]): LoanApplicationEntity[] {
    const processingStrategy = this._paymentMethodDetailEntity.processingStrategy;

    if (!processingStrategy) {
      return loanApplications;
    }

    // The loan stores the order the cascade will actually follow, fixed once when the applications
    // were created. It is the only source that matches what the backend will do, so it wins over
    // the configured lender list; the endpoint that returns the applications is unordered, and
    // showing a different order is worst of all in manual mode, where a person picks from it.
    const orderedIds = this._paymentMethodDetailEntity.loanApplicationIds;
    if (orderedIds?.length) {
      const byId = new Map(loanApplications.map((loanApplication) => [loanApplication.id, loanApplication]));
      const ordered = orderedIds
        .map((id) => byId.get(id))
        .filter((loanApplication): loanApplication is LoanApplicationEntity => loanApplication !== undefined);
      // Never drop a row: anything the stored order does not name keeps its original position.
      const remaining = loanApplications.filter((loanApplication) => !orderedIds.includes(loanApplication.id));

      return [...ordered, ...remaining];
    }

    if (processingStrategy.details?.orderBy === ChooseStrategyType.MANUAL) {
      const sortedLoanApplications: LoanApplicationEntity[] = [];
      processingStrategy.details.options.forEach((option) => {
        loanApplications.forEach((loanApplication) => {
          if (option.lenderName === loanApplication.lenderName) {
            sortedLoanApplications.push(loanApplication);
          }
        });
      });
      return sortedLoanApplications;
    }

    return loanApplications;
  }

  protected setProcessingStatus(sortedLoanApplications: LoanApplicationEntity[]): LoanApplicationEntity[] {
    const processedLoanApplications: LoanApplicationEntity[] = [];

    let firstProcessingFound = false;

    const stopProcessing = sortedLoanApplications.some((loanApplication) => this.shouldStopProcessing(loanApplication));

    // In manual processing mode nothing is ever sent on its own, so the first unprocessed
    // application is waiting for a person, not for a lender. Marking it as in flight showed a
    // spinner for a call nobody made and hid the Process button on the very row to act on.
    const autoSendsToTheNextLender = !this.isManualProcessingMode();

    sortedLoanApplications.forEach((loanApplication) => {
      if (stopProcessing) {
        loanApplication.isProcessing = false;
        loanApplication.alreadyProcessed = loanApplication.isNotProcessed() ? false : true;
      } else {
        if (!firstProcessingFound) {
          if (loanApplication.isNotProcessed()) {
            firstProcessingFound = true;
            loanApplication.isProcessing = autoSendsToTheNextLender;
            loanApplication.alreadyProcessed = false;
          } else {
            loanApplication.isProcessing = false;
            loanApplication.alreadyProcessed = true;
          }
        } else {
          loanApplication.isProcessing = false;
          loanApplication.alreadyProcessed = loanApplication.isNotProcessed() ? false : true;
        }
      }

      processedLoanApplications.push(loanApplication);
    });

    return processedLoanApplications;
  }

  public allowProcessing(loanApplicationId: string): boolean {
    if (!this._loanApplications) return false;
    if (this.allowProcessingLoanApplications() === false) return false;
    const loanApplication = this._loanApplications.find((loanApplication) => loanApplication.id === loanApplicationId);
    if (!loanApplication) return false;
    if (loanApplication.isNotProcessed()) return true;
    if (loanApplication.hasErrors()) return true;
    return false;
  }

  public getStopCondition(): StopCondition {
    const stopCondition = this._paymentMethodDetailEntity.processingStrategy?.details?.stopCondition;

    if (!stopCondition) {
      return this.DEFAULT_STOP_CONDITION;
    }

    return stopCondition;
  }

  /**
   * One rail descriptor per loan application, in the order they are shown (AUTO-156).
   *
   * Everything is derived from state the entity already has, so the rail needs no new data from
   * the API. "current" is the first unprocessed application, and only while the cascade is still
   * running: once one has met the stop condition nothing else will be consulted, so the rest stay
   * pending. In manual processing mode nobody advances on its own, so there is no "current" at all.
   */
  public railRows(): RailRow[] {
    const loanApplications = this._loanApplications ?? [];
    const dashed = this.isManualProcessingMode();

    // The green bar means "the cascade ended here", so only one row can carry it, and only when
    // there is a cascade at all. Two traps live here: shouldStopProcessing() answers true for
    // EVERY processed application once the agent's stop condition is 'error' (the auto agents), and
    // in manual processing mode nothing advances on its own, so nothing can stop either.
    const stopIndex = dashed
      ? -1
      : loanApplications.findIndex((loanApplication) => this.shouldStopProcessing(loanApplication));
    const hasStopped = stopIndex !== -1;
    let currentTaken = false;

    return loanApplications.map((loanApplication, index) => {
      const isStop = index === stopIndex;

      if (!loanApplication.isNotProcessed()) {
        return { step: 'done', isStop, dashed };
      }
      if (dashed) {
        return { step: 'suggested', isStop, dashed };
      }
      if (!hasStopped && !currentTaken) {
        currentTaken = true;
        return { step: 'current', isStop, dashed };
      }

      return { step: 'pending', isStop, dashed };
    });
  }

  public shouldStopProcessing(loanApplication: LoanApplicationEntity): boolean {
    const stopCondition = this.getStopCondition();

    if (loanApplication.isSelected()) {
      return true;
    }

    if (loanApplication.isNotProcessed()) {
      return false;
    }

    if (stopCondition === StopCondition.ERROR) {
      return true;
    }

    const lenderApprovalStatus = loanApplication.status;

    if (!lenderApprovalStatus) {
      return false;
    }

    const lenderApprovalStatuses = this.stopConditionMap.get(stopCondition)!;

    if (lenderApprovalStatuses.includes(lenderApprovalStatus)) {
      return true;
    }

    return false;
  }

  //private nextLoanApplicationToProcess(): LoanApplicationEntity | undefined {
  //  if (!this._loanApplications) {
  //    return;
  //  }

  //  const candidateNextLoanApplication = this._loanApplications.find((loanApplication) =>
  //    loanApplication.isNotProcessed(),
  //  );

  //  if (candidateNextLoanApplication) {
  //    return candidateNextLoanApplication;
  //  }

  //  return this._loanApplications.find((loanApplication) => loanApplication.hasErrors());
  //}

  private allowProcessingLoanApplications(): boolean {
    if (!this._loanApplications) return false;

    let allowProcessing = true;
    this._loanApplications.forEach((loanApplication) => {
      //if (this.shouldStopProcessing(loanApplication)) {
      //  allowProcessing = false;
      //}
      if (loanApplication.isYes() && loanApplication.isSelected()) {
        allowProcessing = false;
      }
    });

    return allowProcessing;
  }

  public clone(): this {
    const clone = new WaterfallProcessingStrategyEntity(this._paymentMethodDetailEntity);

    // Copy all properties from this to clone
    Object.assign(clone, this);

    return clone as this;
  }
}
